import React, { useState, useEffect } from 'react';
import { Button } from '@saviynt/design-system';
import PropTypes from 'prop-types';
import useStyles from './styles/SaviyntConfigForm.styles';

/**
 * SaviyntConfigForm
 */
function SaviyntConfigForm({ isOpen, onClose, title, data, onConfirm }) {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    connection: {
      connectionName: '',
      description: '',
    },
    securitySystem: {
      systemname: '',
      displayName: '',
    },
    endpoint: {
      endpointname: '',
      displayName: '',
    },
  });

  useEffect(() => {
    if (data && isOpen) {
      setFormData({
        connection: {
          connectionName: data.connection?.connectionName || data.connection?.connection_name || '',
          description:
            data.connection?.description ||
            data.connection?.connectionDescription ||
            `Connection configuration for ${title}`,
        },
        securitySystem: {
          systemname: data.securitySystem?.systemname || '',
          displayName: data.securitySystem?.displayName || data.securitySystem?.display_name || '',
        },
        endpoint: {
          endpointname: data.endpoint?.endpointname || data.endpoint?.endpoint_name || '',
          displayName: data.endpoint?.displayName || data.endpoint?.display_name || '',
        },
      });
    }
  }, [data, isOpen, title]);

  // Input validation and sanitization
  const sanitizeInput = (value) => {
    if (typeof value !== 'string') return '';
    // Remove potentially dangerous characters
    return value.replace(/[<>"'&]/g, '').trim();
  };

  const handleInputChange = (section, field, value) => {
    // Sanitize input to prevent XSS
    const sanitizedValue = sanitizeInput(value);

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: sanitizedValue,
      },
    }));
  };

  const handleConfirm = () => {
    const updatedConfig = {
      connection: {
        ...data.connection,
        connectionName: formData.connection.connectionName,
        ...(data.connection?.description !== undefined
          ? { description: formData.connection.description }
          : { connectionDescription: formData.connection.description }),
      },
      securitySystem: {
        ...data.securitySystem,
        systemname: formData.securitySystem.systemname,
        displayName: formData.securitySystem.displayName,
      },
      endpoint: {
        ...data.endpoint,
        endpointname: formData.endpoint.endpointname,
        displayName: formData.endpoint.displayName,
      },
    };

    onConfirm(updatedConfig);
  };

  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <h3 className={classes.title}>Review and confirm below parameters</h3>
          <button onClick={onClose} className={classes.closeButton} type="button">
            ×
          </button>
        </div>

        <div className={classes.content}>
          <div className={classes.section}>
            <h4 className={classes.sectionTitle}>Connection</h4>

            <label htmlFor="connection-name" className={classes.field}>
              <span className={classes.label}>Name</span>
              <input
                id="connection-name"
                type="text"
                value={formData.connection.connectionName}
                onChange={(e) => handleInputChange('connection', 'connectionName', e.target.value)}
                className={classes.input}
              />
            </label>

            <label htmlFor="connection-description" className={classes.field}>
              <span className={classes.label}>Description</span>
              <textarea
                id="connection-description"
                value={formData.connection.description}
                onChange={(e) => handleInputChange('connection', 'description', e.target.value)}
                rows={2}
                className={classes.textarea}
              />
            </label>
          </div>

          <div className={classes.section}>
            <h4 className={classes.sectionTitle}>Security System</h4>

            <label htmlFor="security-name" className={classes.field}>
              <span className={classes.label}>Name</span>
              <input
                id="security-name"
                type="text"
                value={formData.securitySystem.systemname}
                onChange={(e) => handleInputChange('securitySystem', 'systemname', e.target.value)}
                className={classes.input}
              />
            </label>

            <label htmlFor="security-display" className={classes.field}>
              <span className={classes.label}>Display Name</span>
              <input
                id="security-display"
                type="text"
                value={formData.securitySystem.displayName}
                onChange={(e) => handleInputChange('securitySystem', 'displayName', e.target.value)}
                className={classes.input}
              />
            </label>
          </div>

          <div className={classes.section}>
            <h4 className={classes.sectionTitle}>Endpoint</h4>

            <label htmlFor="endpoint-name" className={classes.field}>
              <span className={classes.label}>Name</span>
              <input
                id="endpoint-name"
                type="text"
                value={formData.endpoint.endpointname}
                onChange={(e) => handleInputChange('endpoint', 'endpointname', e.target.value)}
                className={classes.input}
              />
            </label>

            <label htmlFor="endpoint-display" className={classes.field}>
              <span className={classes.label}>Display Name</span>
              <input
                id="endpoint-display"
                type="text"
                value={formData.endpoint.displayName}
                onChange={(e) => handleInputChange('endpoint', 'displayName', e.target.value)}
                className={classes.input}
              />
            </label>
          </div>
        </div>

        <div className={classes.footer}>
          <Button variant="outlined" onClick={onClose} size="small">
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirm} size="small">
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

SaviyntConfigForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    connection: PropTypes.shape({
      connectionName: PropTypes.string,
      connection_name: PropTypes.string,
      description: PropTypes.string,
      connectionDescription: PropTypes.string,
    }),
    securitySystem: PropTypes.shape({
      systemname: PropTypes.string,
      displayName: PropTypes.string,
      display_name: PropTypes.string,
    }),
    endpoint: PropTypes.shape({
      endpointname: PropTypes.string,
      endpoint_name: PropTypes.string,
      displayName: PropTypes.string,
      display_name: PropTypes.string,
    }),
  }),
  onConfirm: PropTypes.func.isRequired,
};

SaviyntConfigForm.defaultProps = {
  data: null,
};

export default SaviyntConfigForm;
